import { createClient } from '@supabase/supabase-js';

const supabaseUrl = (import.meta.env.VITE_SUPABASE_URL || '').trim();
const supabasePublishableKey = (import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY || '').trim();
const googleEnabled = /^true$/i.test((import.meta.env.VITE_SUPABASE_GOOGLE_ENABLED || '').trim());
const appleEnabled = /^true$/i.test((import.meta.env.VITE_SUPABASE_APPLE_ENABLED || '').trim());
const appleMode = ((import.meta.env.VITE_SUPABASE_APPLE_MODE || 'oauth').trim().toLowerCase());
const appleServiceId = (import.meta.env.VITE_SUPABASE_APPLE_SERVICE_ID || '').trim();
const appleJsScriptUrl =
  'https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js';

export type SupabaseSocialProvider = 'google' | 'apple';
export type SupabaseProviderSignInResult = {
  data: unknown;
  error: { message: string } | Error | null;
  provider: SupabaseSocialProvider;
  url: string | null;
};

declare global {
  interface Window {
    AppleID?: {
      auth: {
        init(config: {
          clientId: string;
          scope: string;
          redirectURI: string;
          state?: string;
          nonce?: string;
          usePopup?: boolean;
        }): void;
        signIn(): Promise<{
          authorization?: {
            id_token?: string;
          };
          id_token?: string;
          user?: {
            name?: {
              firstName?: string;
              middleName?: string;
              lastName?: string;
            };
          };
        }>;
      };
    };
  }
}

export const isSupabaseAuthConfigured = () =>
  Boolean(supabaseUrl) && Boolean(supabasePublishableKey);

export const isSupabaseProviderEnabled = (provider: SupabaseSocialProvider) => {
  if (provider === 'google') return googleEnabled;
  if (provider === 'apple') return appleEnabled;
  return false;
};

export const getEnabledSupabaseProviders = (): SupabaseSocialProvider[] =>
  (['google', 'apple'] as const).filter(provider => isSupabaseProviderEnabled(provider));

export const isClassicAppleJsEnabled = () =>
  appleEnabled && appleMode === 'classic_js' && Boolean(appleServiceId);

export const supabase = isSupabaseAuthConfigured()
  ? createClient(supabaseUrl, supabasePublishableKey, {
      auth: {
        autoRefreshToken: true,
        persistSession: true,
        detectSessionInUrl: true,
      },
    })
  : null;

function requireSupabase() {
  if (!supabase) {
    throw new Error('Supabase Auth is not configured.');
  }

  return supabase;
}

async function loadAppleJs() {
  if (typeof window === 'undefined') {
    throw new Error('Apple Sign In is only available in the browser.');
  }

  if (window.AppleID?.auth) {
    return window.AppleID;
  }

  await new Promise<void>((resolve, reject) => {
    const existingScript = document.querySelector<HTMLScriptElement>(
      `script[src="${appleJsScriptUrl}"]`,
    );

    if (existingScript) {
      existingScript.addEventListener('load', () => resolve(), { once: true });
      existingScript.addEventListener(
        'error',
        () => reject(new Error('Failed to load Apple Sign In script.')),
        { once: true },
      );
      return;
    }

    const script = document.createElement('script');
    script.src = appleJsScriptUrl;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('Failed to load Apple Sign In script.'));
    document.head.appendChild(script);
  });

  if (!window.AppleID?.auth) {
    throw new Error('Apple Sign In is unavailable in this browser.');
  }

  return window.AppleID;
}

async function signInWithAppleJs(redirectTo: string): Promise<SupabaseProviderSignInResult> {
  const client = requireSupabase();
  const AppleID = await loadAppleJs();
  const nonce = crypto.randomUUID();
  const state = crypto.randomUUID();

  AppleID.auth.init({
    clientId: appleServiceId,
    scope: 'name email',
    redirectURI: redirectTo,
    state,
    nonce,
    usePopup: true,
  });

  const appleResponse = await AppleID.auth.signIn();
  const idToken =
    appleResponse.authorization?.id_token || appleResponse.id_token || null;

  if (!idToken) {
    return {
      data: null,
      error: new Error('Apple did not return an identity token.'),
      provider: 'apple',
      url: null,
    };
  }

  const authResult = await client.auth.signInWithIdToken({
    provider: 'apple',
    token: idToken,
    nonce,
  });

  if (!authResult.error && appleResponse.user?.name) {
    const fullName = [
      appleResponse.user.name.firstName,
      appleResponse.user.name.middleName,
      appleResponse.user.name.lastName,
    ]
      .filter(Boolean)
      .join(' ')
      .trim();

    await client.auth.updateUser({
      data: {
        ...(fullName ? { full_name: fullName } : {}),
        ...(appleResponse.user.name.firstName
          ? { given_name: appleResponse.user.name.firstName }
          : {}),
        ...(appleResponse.user.name.lastName
          ? { family_name: appleResponse.user.name.lastName }
          : {}),
      },
    });
  }

  return {
    data: authResult.data,
    error: authResult.error,
    provider: 'apple',
    url: null,
  };
}

export async function getSupabaseAccessToken(): Promise<string | null> {
  if (!supabase) return null;

  const { data, error } = await supabase.auth.getSession();
  if (error) {
    throw error;
  }

  return data.session?.access_token ?? null;
}

export async function signInWithSupabase(email: string, password: string) {
  const client = requireSupabase();
  return client.auth.signInWithPassword({
    email: email.trim(),
    password,
  });
}

export async function signUpWithSupabase(email: string, password: string) {
  const client = requireSupabase();
  return client.auth.signUp({
    email: email.trim(),
    password,
  });
}

export async function signOutFromSupabase(): Promise<void> {
  if (!supabase) return;
  await supabase.auth.signOut();
}

export async function signInWithSupabaseProvider(
  provider: SupabaseSocialProvider,
  redirectTo: string,
): Promise<SupabaseProviderSignInResult> {
  const client = requireSupabase();

  if (provider === 'apple' && isClassicAppleJsEnabled()) {
    return signInWithAppleJs(redirectTo);
  }

  const result = await client.auth.signInWithOAuth({
    provider,
    options: {
      redirectTo,
    },
  });

  return {
    data: result.data,
    error: result.error,
    provider,
    url: result.data?.url ?? null,
  };
}
